import React from 'react';
import { Formik, Form, useField } from "formik";
import Turnstile, { useTurnstile } from 'react-turnstile';
import * as Yup from 'yup';
import { getHmacCredentials } from '../utilities/hmac';
import { getTrackingCodes } from '../../shared/trackingCodes';

const contactFormId = 'reactContactForm';

const ContactSchema = Yup.object().shape({
	firstname: Yup.string()
		.required('Required'),
	email: Yup.string().email('Invalid email').required('Required'),
	company: Yup.string()
		.required('Required'),
	message: Yup.string()
		.required('Required'),
});

const TextInput = ({ label, ...props }) => {
	const [field, meta] = useField(props);
	return (
		<div className="mb-1">
			<label htmlFor={props.id || props.name}>{label}{props.required ? <span className="required">*</span> : null}</label>
			<input className="text-input" {...field} {...props} />
			{meta.touched && meta.error ? (
				<div className="error">{meta.error}</div>
			) : null}
		</div>
	);
};

const TextArea = ({ label, ...props }) => {
	const [field, meta] = useField(props);
	return (
		<div className="mb-1">
			<label htmlFor={props.id || props.name}>{label}{props.required ? <span className="required">*</span> : null}</label>
			<textarea rows="2" className="text-area" {...field} {...props} />
			{meta.touched && meta.error ? (
				<div className="error">{meta.error}</div>
			) : null}
		</div>
	);
};

const resetForm = (actions, status) => {
	actions.resetForm({ status })
	const fields = document.querySelector('#' + contactFormId).querySelectorAll('input, textarea');
	fields.forEach((field) => field.classList.remove('has-value'));
}

const onSubmit = async (data, actions) => {
	const { turnstileToken, firstname, email, company, message, site } = data;

  const trackingCodes = getTrackingCodes();

	const requestMethod = 'POST';
	const requestURI = '/contact-submissions';

	const hmacAuth = getHmacCredentials({
		requestMethod,
		requestURI,
		turnstileToken
	});

	const options = {
		method: requestMethod,
		body: JSON.stringify({ firstname, email, company, message, site, ...trackingCodes }),
		headers: {
			"Authorization": hmacAuth.token,
			"Content-Type": "application/json; charset=utf-8",
			"x-api-key": process.env.X_API_KEY,
			"x-api-date-time": hmacAuth.date
		}
	};

	try {
		const rawResponse = await fetch(`${process.env.X_API_HOST}${requestURI}`, options);

		if (rawResponse.status > 199 && rawResponse.status < 300) {

			if (window.dataLayer) {
				// Fire event for tracking
				window.dataLayer.push({'event': 'form_submit'});
			}

			resetForm(actions, { isSubmitSuccessful: true });

			const element = document.getElementById(contactFormId);
			const elementPosition = element.getBoundingClientRect().top;
		  const offset = window.pageYOffset - 120;

			window.scrollTo({
		    top: elementPosition + offset,
		    behavior: "smooth"
		  });

			return;
		}
	} catch (error) {
	}

	resetForm(actions, { isSubmitSuccessful: false });
};

const ContactForm = ({ site }) => {
	const turnstile = useTurnstile();

	const siteValue = typeof site == 'string' ? site.toUpperCase() : '';

	return (
		<Formik
			initialValues={{ firstname: "", email: "", company: "", message: "", turnstileToken: null, site: siteValue }}
			initialStatus={{ isSubmitSuccessful: null }}
			validationSchema={ContactSchema}
			onSubmit={async (data, actions) => onSubmit(data, actions)}
		>

			{({ errors, status, isValid, isSubmitting, setFieldValue, setFieldError }) => (
				<Form id={contactFormId}>

					{status.isSubmitSuccessful == true && (
						<div className={'success-message mb-2'}>
							<h3>
								Thank you for getting in touch.<br/>
								We'll get back to you soon.
							</h3>
						</div>
					)}

					{status.isSubmitSuccessful == false && (
						<div className={'fail-message mb-2'}>
							<h3>
								There was a problem submitting the form.<br/>
								Please try again or send an email to one of our offices above.
							</h3>
						</div>
					)}

					<div className={status.isSubmitSuccessful ? ('d-none') : null}>
						<TextInput
							label="Name"
							name="firstname"
							type="text"
							required
						/>

						<TextInput
							label="Email"
							name="email"
							type="text"
							required
						/>

						<TextInput
							label="Company"
							name="company"
							type="text"
							required
						/>

						<TextArea
							label="Message"
							name="message"
							type="text"
							required
						/>

						<div>
							<button className="contact-button button button--cta-white" type="submit" disabled={!isValid || isSubmitting}>
								Send <div className="arrow-pseudo arrow-pseudo--semibold arrow-pseudo--move d-none d-lg-inline-block mb-0"></div>
							</button>
						</div>

						<div className='mb-1'>
							&nbsp;
							<Turnstile
								sitekey={process.env.CLOUDFLARE_TURNSTILE_SITE_KEY}
								executution="execute"
								appearance={'interaction-only'}
								onVerify={(token) => {
									try {
										setFieldValue('turnstileToken', token);
									} catch (error) {
										setFieldError('turnstileToken', 'Failed anti-spam validation');
									}
								}}
								onError={(error) => {
									setFieldError('turnstileToken', 'Failed to initiate anti-spam validation');
								}}
							/>
							{errors.turnstileToken ? (
								<div className="error">{errors.turnstileToken}</div>
							) : null}
						</div>
					</div>
				</Form>
			)}
		</Formik>
	);
}

export default ContactForm;
